// Fresh packing station: weigh-queue ordering and grouping, the gram field, live recalculation
// (with the shared catch-weight maths, never re-implemented) and "every line weighed" detection.
// Pure: no DOM, no Intl. The server's WeighResult is authoritative; this is display only.

import { finalLine, priceForGrams } from '@popcart/core/v1';
import { peso } from '../../../lib/format.js';

/** @typedef {import('@popcart/core/v1').WeighTask} WeighTask */
/** @typedef {import('@popcart/core/v1').WeighResult} WeighResult */
/** @typedef {import('@popcart/core/v1').ShopQueueItem} ShopQueueItem */
/** @typedef {import('@popcart/core/v1').ShopOrderStatus} ShopOrderStatus */

/**
 * @typedef {object} WeighOrder
 * @property {string} shopOrderId
 * @property {string} orderId
 * @property {string} orderCode
 * @property {import('@popcart/core/v1').Speed} speed
 * @property {string} placedAt
 * @property {string | null} customerFirstName  from the shop queue (WeighTask doesn't carry it)
 * @property {boolean} late
 * @property {ShopOrderStatus | null} status     null when the queue hasn't loaded this order yet
 * @property {import('@popcart/core/v1').ProductLine[]} lines  every line in the order (Fresh + Grill/Mart)
 * @property {WeighTask[]} tasks
 * @property {number} weighedCount
 * @property {boolean} allWeighed
 */

export const MIN_GRAMS = 1;
export const MAX_GRAMS = 100000;

/** Express first, then oldest, then order code (stable for equal times). @param {{ speed: string; placedAt: string; orderCode: string }} a @param {{ speed: string; placedAt: string; orderCode: string }} b */
function byUrgency(a, b) {
  if (a.speed !== b.speed) return a.speed === 'express' ? -1 : 1;
  if (a.placedAt !== b.placedAt) return a.placedAt < b.placedAt ? -1 : 1;
  return a.orderCode < b.orderCode ? -1 : a.orderCode > b.orderCode ? 1 : 0;
}

/**
 * Put fresher local results over the server's tasks (a save lands before the next poll).
 * @param {WeighTask[]} tasks
 * @param {Record<string, WeighResult>} results orderItemId → latest WeighResult from this screen
 * @returns {WeighTask[]}
 */
export function applyResults(tasks, results) {
  return tasks.map((t) => {
    const r = results[t.orderItemId];
    if (!r) return t;
    const w = t.weighed;
    // Keep whichever is newer: a later poll may already carry a re-weigh from another tablet.
    if (w && w.weighedAt > r.weighedAt) return t;
    return {
      ...t,
      weighed: { actualGrams: r.actualGrams, finalLineCentavos: r.finalLineCentavos, deltaCentavos: r.deltaCentavos, photoUrl: r.photoUrl, weighedAt: r.weighedAt },
    };
  });
}

/**
 * Group weigh tasks by shop order, Express first then oldest. Lines keep the server's order inside
 * an order (they don't jump about as they're weighed).
 * @param {WeighTask[]} tasks
 * @param {ShopQueueItem[]} [queue] the shop queue, for customer first name, late flag and status
 * @returns {WeighOrder[]}
 */
export function groupTasks(tasks, queue = []) {
  /** @type {Map<string, ShopQueueItem>} */
  const q = new Map();
  for (const item of queue) q.set(item.shopOrderId, item);
  /** @type {Map<string, WeighOrder>} */
  const groups = new Map();
  for (const t of tasks) {
    let g = groups.get(t.shopOrderId);
    if (!g) {
      const qi = q.get(t.shopOrderId);
      g = {
        shopOrderId: t.shopOrderId,
        orderId: t.orderId,
        orderCode: t.orderCode,
        speed: t.speed,
        placedAt: t.placedAt,
        customerFirstName: qi?.customerFirstName ?? null,
        late: qi?.late ?? false,
        status: qi?.status ?? null,
        lines: qi?.lines ?? ['fresh'],
        tasks: [],
        weighedCount: 0,
        allWeighed: false,
      };
      groups.set(t.shopOrderId, g);
    }
    g.tasks.push(t);
  }
  const out = [...groups.values()];
  for (const g of out) {
    g.weighedCount = g.tasks.filter((t) => t.weighed).length;
    g.allWeighed = isAllWeighed(g.tasks);
  }
  return out.sort(byUrgency);
}

/** Every weight line of the order has a weigh-in. @param {WeighTask[]} tasks */
export function isAllWeighed(tasks) {
  return tasks.length > 0 && tasks.every((t) => !!t.weighed);
}

/** All tasks in screen order. @param {WeighOrder[]} groups */
export function flatTasks(groups) {
  return groups.flatMap((g) => g.tasks);
}

/**
 * The task to open after `currentId` is saved: the next unweighed task after it in screen order,
 * wrapping round; null when nothing is left to weigh.
 * @param {WeighOrder[]} groups
 * @param {string | null} currentId
 * @returns {string | null}
 */
export function nextTaskId(groups, currentId) {
  const all = flatTasks(groups);
  const i = currentId ? all.findIndex((t) => t.orderItemId === currentId) : -1;
  const order = i < 0 ? all : [...all.slice(i + 1), ...all.slice(0, i)];
  const next = order.find((t) => !t.weighed && t.orderItemId !== currentId);
  return next ? next.orderItemId : null;
}

/** Unweighed tasks across all orders. @param {WeighOrder[]} groups */
export function toWeighCount(groups) {
  return flatTasks(groups).filter((t) => !t.weighed).length;
}

/**
 * Read the gram field: whole grams 1–100,000. Spaces and a trailing "g" are ignored; commas are
 * accepted only as thousands separators ("1,250 g").
 * @param {string} input
 * @returns {{ ok: true; grams: number } | { ok: false; empty: boolean; error: string }}
 */
export function parseGrams(input) {
  let s = String(input ?? '').replace(/[\s  ]+/g, '').toLowerCase();
  if (s.endsWith('grams')) s = s.slice(0, -5);
  else if (s.endsWith('g')) s = s.slice(0, -1);
  if (!s) return { ok: false, empty: true, error: 'Enter the weight in grams.' };
  if (/^\d{1,3}(,\d{3})+$/.test(s)) s = s.replace(/,/g, '');
  if (/^\d+[.,]\d*$/.test(s)) return { ok: false, empty: false, error: 'Whole grams only (no decimals).' };
  if (!/^\d+$/.test(s)) return { ok: false, empty: false, error: 'Numbers only, in grams.' };
  const grams = Number(s);
  if (grams < MIN_GRAMS) return { ok: false, empty: false, error: 'The weight must be at least 1 g.' };
  if (grams > MAX_GRAMS) return { ok: false, empty: false, error: 'That is over 100 kg. Check the scale reading.' };
  return { ok: true, grams };
}

/**
 * The prep fee per unit, recovered from the task (WeighTask doesn't carry it; the estimate is
 * qty × (price for the per-unit grams + prep fee)). Never negative.
 * @param {Pick<WeighTask, 'qty' | 'estGramsTotal' | 'estLineCentavos' | 'pricePerKgCentavos'>} t
 */
export function prepFeePerUnit(t) {
  if (!(t.qty >= 1)) return 0;
  const perUnitG = Math.round(t.estGramsTotal / t.qty);
  if (!(perUnitG > 0)) return 0;
  const fee = Math.round(t.estLineCentavos / t.qty) - priceForGrams(perUnitG, t.pricePerKgCentavos);
  return Math.max(0, fee);
}

/** "₱680/kg". @param {number} c */
export function perKg(c) {
  return `${peso(c)}/kg`;
}

/** "1,250 g" / "18.4 kg" style grams for labels. @param {number} g */
export function gramsLabel(g) {
  if (!Number.isFinite(g)) return '—';
  const s = String(Math.round(g));
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ',';
    out += s[i];
  }
  return `${out} g`;
}

/**
 * @typedef {object} Recalc
 * @property {number} grams
 * @property {number} estimateCentavos
 * @property {number} finalCentavos
 * @property {number} rawCentavos
 * @property {number} capCentavos
 * @property {number} deltaCentavos
 * @property {number} shopAbsorbedCentavos
 * @property {boolean} capped
 * @property {string} finalText     "Final ₱348.16"
 * @property {string} deltaText     "₱25.84 less than estimate"
 * @property {'neutral' | 'warning'} tone   warning only when the shop absorbs
 * @property {string} pill          the status pill text
 * @property {string | null} check  a sanity warning when the reading is far from the order
 */

/**
 * Live recalculation for the weigh panel, from the shared finalLine maths.
 * @param {WeighTask} t
 * @param {number} grams a validated reading (see parseGrams)
 * @returns {Recalc}
 */
export function recalc(t, grams) {
  const r = finalLine({
    pricePerKgCentavos: t.pricePerKgCentavos,
    qty: t.qty,
    actualGrams: grams,
    prepFeeCentavos: prepFeePerUnit(t),
    estLineCentavos: t.estLineCentavos,
    tolerancePct: t.tolerancePct,
  });
  const capped = r.shopAbsorbedCentavos > 0;
  const d = r.deltaCentavos;
  const deltaText = d < 0 ? `${peso(-d)} less than estimate` : d > 0 ? `${peso(d)} more than estimate` : 'Same as the estimate';
  return {
    grams,
    estimateCentavos: t.estLineCentavos,
    finalCentavos: r.finalCentavos,
    rawCentavos: r.rawCentavos,
    capCentavos: r.capCentavos,
    deltaCentavos: d,
    shopAbsorbedCentavos: r.shopAbsorbedCentavos,
    capped,
    finalText: `Final ${peso(r.finalCentavos)}`,
    deltaText,
    tone: capped ? 'warning' : 'neutral',
    pill: capped ? `Capped: shop absorbs ${peso(r.shopAbsorbedCentavos)}` : deltaText,
    check: sanityCheck(t, grams),
  };
}

/**
 * A nudge when the reading is far from what was ordered (a typo like 5120 for 512).
 * @param {Pick<WeighTask, 'estGramsTotal'>} t @param {number} grams
 * @returns {string | null}
 */
export function sanityCheck(t, grams) {
  if (!(t.estGramsTotal > 0)) return null;
  if (grams >= t.estGramsTotal * 2) return `Check the reading: ${gramsLabel(grams)} is over twice the ${gramsLabel(t.estGramsTotal)} ordered.`;
  if (grams * 2 <= t.estGramsTotal) return `Check the reading: ${gramsLabel(grams)} is under half the ${gramsLabel(t.estGramsTotal)} ordered.`;
  return null;
}

/**
 * The steps from an order's status to 'ready'. Weighing the last Fresh line means the order is
 * being packed, so an accepted (or new) order is moved through preparing on the way.
 * @param {ShopOrderStatus | null} status
 * @returns {('accepted' | 'preparing' | 'ready')[] | null} null when it can't (or needn't) move
 */
export function readySteps(status) {
  if (status === 'preparing') return ['ready'];
  if (status === 'accepted') return ['preparing', 'ready'];
  if (status === 'placed') return ['accepted', 'preparing', 'ready'];
  return null;
}

/**
 * What the order's footer shows: the Volt "Ready for rider", a waiting note, or done.
 * @param {WeighOrder} g
 * @returns {{ kind: 'ready'; steps: ('accepted' | 'preparing' | 'ready')[] } | { kind: 'weigh'; left: number } | { kind: 'done' } | { kind: 'unknown' }}
 */
export function orderAction(g) {
  if (g.status === 'ready' || g.status === 'picked_up' || g.status === 'delivered') return { kind: 'done' };
  if (!g.allWeighed) return { kind: 'weigh', left: g.tasks.length - g.weighedCount };
  const steps = readySteps(g.status);
  return steps ? { kind: 'ready', steps } : { kind: 'unknown' };
}

/** The 409 the API returns for advancing with unweighed lines. @param {unknown} err */
export function isUnweighedConflict(err) {
  const e = /** @type {{ status?: number; message?: string } | null} */ (err);
  return !!e && e.status === 409 && /weigh/i.test(e.message || '');
}

/**
 * What to announce (aria-live) and show after a save, from the server's WeighResult.
 * @param {WeighTask} t @param {WeighResult} r
 */
export function savedMessage(t, r) {
  const parts = [`${t.productName}: ${gramsLabel(r.actualGrams)} saved`, `final ${peso(r.finalLineCentavos)}`];
  if (r.shopAbsorbedCentavos > 0) parts.push(`shop absorbs ${peso(r.shopAbsorbedCentavos)}`);
  if (r.allWeighed) {
    if (r.codDueCentavos !== null && r.codDueCentavos !== undefined) parts.push(`all weighed, COD due is now ${peso(r.codDueCentavos)}`);
    else if (r.refundCentavos) parts.push(`all weighed, ${peso(r.refundCentavos)} goes back to the customer`);
    else parts.push('all weighed');
  }
  return parts.join(' · ') + '.';
}

/**
 * Photo checks before upload (the API takes jpg/png/webp up to 8 MB; we resize before sending).
 * @param {{ type: string; size: number } | null} f
 * @returns {string | null} an error, or null when it's fine
 */
export function photoProblem(f) {
  if (!f) return null;
  if (!/^image\//.test(f.type)) return 'That file isn’t a photo. Take a picture with the camera.';
  if (f.size > 25 * 1024 * 1024) return 'That photo is too large. Take it again.';
  return null;
}

/**
 * Scale (w, h) down to fit inside `max` on the long edge, never up.
 * @param {number} w @param {number} h @param {number} max
 */
export function fitWithin(w, h, max) {
  const long = Math.max(w, h);
  if (!(long > max)) return { w, h };
  const k = max / long;
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}
